/* Progressive enhancement only: the server supplies every image and caption. */
class MaisonApprovedGallery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.rail = this.querySelector('[data-mdg-rail]');
    this.slides = [...this.querySelectorAll('[data-mdg-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-mdg-index]')];
    if (!this.rail || !this.slides.length) return;
    this.current = 0;
    this.prev = this.querySelector('[data-mdg-prev]');
    this.next = this.querySelector('[data-mdg-next]');
    this.counter = this.querySelector('[data-mdg-counter]');
    // Section rendering can insert a second instance with the same server IDs.
    const uid = `mdg-instance-${++MaisonApprovedGallery.count}`;
    this.rail.id = `${uid}-rail`;
    [this.prev, this.next].forEach(button => button.setAttribute('aria-controls', this.rail.id));
    this.slides.forEach((slide, i) => {
      slide.id = `${uid}-${i + 1}`;
      this.thumbs[i].href = `#${slide.id}`;
    });
    this.querySelector('[data-mdg-controls]').hidden = false;
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', event => {
        event.preventDefault();
        this.select(i);
      }, { signal });
      thumb.addEventListener('keydown', event => {
        const target = this.keyTarget(event.key, i);
        if (target === null) return;
        event.preventDefault();
        this.select(target);
        this.thumbs[target].focus({ preventScroll:true });
      }, { signal });
    });
    this.prev.addEventListener('click', () => this.select(this.current - 1), { signal });
    this.next.addEventListener('click', () => this.select(this.current + 1), { signal });
    this.rail.addEventListener('keydown', event => {
      if (event.target !== this.rail) return;
      const target = this.keyTarget(event.key, this.current);
      if (target === null) return;
      event.preventDefault();
      this.select(target);
    }, { signal });
    this.rail.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        const railLeft = this.rail.getBoundingClientRect().left;
        let closest = 0, distance = Infinity;
        this.slides.forEach((slide, i) => {
          const d = Math.abs(slide.getBoundingClientRect().left - railLeft);
          if (d < distance) { closest = i; distance = d; }
        });
        this.update(closest);
      });
    }, { signal, passive:true });
    this.update(0);
  }
  keyTarget(key, i) {
    const last = this.slides.length - 1;
    if (key === 'ArrowRight') return Math.min(last, i + 1);
    if (key === 'ArrowLeft') return Math.max(0, i - 1);
    if (key === 'Home') return 0;
    if (key === 'End') return last;
    return null;
  }
  select(index) {
    index = Math.max(0, Math.min(index, this.slides.length - 1));
    // Only move the rail, never the page or buybox.
    const left = this.rail.scrollLeft + this.slides[index].getBoundingClientRect().left - this.rail.getBoundingClientRect().left;
    this.rail.scrollTo({ left, behavior: 'instant' });
    this.update(index);
  }
  update(index) {
    this.current = index;
    this.thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current','true');
      else thumb.removeAttribute('aria-current');
    });
    this.slides.forEach((slide, i) => {
      slide.querySelectorAll('a').forEach(a => { a.tabIndex = i === index ? 0 : -1; });
    });
    const text = `${index + 1} / ${this.slides.length}`;
    if (this.counter.textContent !== text) this.counter.textContent = text;
    this.prev.disabled = index === 0;
    this.next.disabled = index === this.slides.length - 1;
    const strip = this.thumbs[index].parentElement;
    const thumb = this.thumbs[index];
    if (thumb.offsetLeft < strip.scrollLeft || thumb.offsetLeft + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
      strip.scrollTo({left: thumb.offsetLeft - strip.offsetLeft, behavior:'instant'});
    }
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    cancelAnimationFrame(this.frame);
  }
}
MaisonApprovedGallery.count = 0;
if (!customElements.get('md-approved-gallery')) customElements.define('md-approved-gallery', MaisonApprovedGallery);
